import type { SubtitleHandler } from '@mimir/common/types/subtitleHandler';
import { type Observable, ObservableState } from '@mimir/common/util/observable';
import { compareFractions, frac } from '@mimir/common/util/integerMath';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import { exceptionToString } from '@mimir/common/util/exception';
import { isAbortError } from '@mimir/common/util/abortControl';
import { readWholeStream } from '@mimir/common/util/readableStream';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { SubtitleCue } from '@mimir/common/types/subtitleCue';
import { parseVtt } from '@mimir/common/subtitles/vttParser';

export class VttHandler implements SubtitleHandler {
  private cues: SubtitleCue[] = [];
  private pending : PendingLoad | null = null;
  private currentSubtitleCue = new ObservableState<SubtitleCue | null>(null);
  private time : Fraction = frac(0, 1);

  public constructor(
    private services: {
      fetch: BasicFetch,
      logger: Logger,
    }
  ) { }

  getCurrentSubtitleCueObs(): Observable<SubtitleCue | null> {
    return this.currentSubtitleCue.getObservable();
  }

  setTime(time: Fraction) {
    this.time = time;
    this.updateCurrentSubtitleCue();
  }

  private updateCurrentSubtitleCue() {
    // TODO: binary search
    const cue = this.cues.find((cue) => {
      return (compareFractions(cue.startTime, this.time) <= 0) && (compareFractions(cue.endTime, this.time) > 0);
    }) ?? null;

    // We explicitly check that it's the same object here as an optimization
    // since the observable will do a deep equal internally. We know the
    // subtitle cues are immutable.
    if (cue !== this.currentSubtitleCue.value) {
      this.currentSubtitleCue.value = cue;
    }
  }

  loadFromUrl(url: string | null): void {
    const abortController = new AbortController();
    if (this.pending) {
      this.pending.abortController.abort();
      this.pending = null;
    }

    if (url === null) {
      this.cues = [];
      this.updateCurrentSubtitleCue();
      return;
    }

    this.pending = {
      promise: this.loadUrlAsync(url, abortController.signal).catch((error) => {
        if (!isAbortError(error)) {
          this.services.logger.error(`Failed to load VTT from url: ${exceptionToString(error)}`);
        }
      }),
      abortController,
    };
  }

  private async loadUrlAsync(url: string, abortSignal: AbortSignal): Promise<void> {
    const response = await this.services.fetch(url, { signal: abortSignal });
    if (!response.ok) {
      throw new Error(`Failed to load VTT from url "${url}": ${response.status} (${response.statusText})`);
    }
    if (response.body === null) {
      throw new Error(`Failed to load VTT from url "${url}": response has no body`);
    }

    const data = await readWholeStream(response.body);

    abortSignal.throwIfAborted();

    this.cues = parseVtt(this.services.logger, data);
    this.updateCurrentSubtitleCue();

    this.services.logger.info(`Successfully loaded VTT from ${url}`);
  }
}

interface PendingLoad {
  abortController: AbortController,
  promise: Promise<void>,
}
